import { BRAND, CONTACT, WA_MESSAGES } from "@/lib/brand";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Menu, X, Lock } from "lucide-react";

const NAV = [
  { id: "home", label: "Home" },
  { id: "store", label: "Store Details" },
  { id: "collections", label: "Our Collections" },
  { id: "instagram", label: "Instagram" },
];

export default function PublicHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const waHref = `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(
    WA_MESSAGES.header,
  )}`;

  return (
    <header
      data-testid="public-header"
      className={`fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-md transition-shadow ${
        scrolled ? "shadow-[0_10px_30px_-15px_rgba(15,23,42,0.15)]" : ""
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8 h-20 flex items-center justify-between gap-4">
        <a
          href="#home"
          data-testid="header-logo"
          className="flex items-center gap-3 shrink-0"
        >
          <img
            src={BRAND.logo}
            alt={BRAND.fullName}
            className="w-11 h-11 rounded-full object-cover ring-2 ring-white shadow-md"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-lg font-black text-slate-900">
              Sky Basket
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#cb05a5] font-semibold">
              Stationery · Gifts
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              data-testid={`nav-${n.id}`}
              className="px-4 py-2 rounded-full text-sm font-medium text-slate-700 hover:text-[#299fe0] hover:bg-[#299fe0]/5 transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1eb857] text-white text-sm font-semibold transition-colors shadow-sm"
          >
            <MessageCircle size={16} />
            WhatsApp
          </a>
          <Link
            to="/admin/login"
            data-testid="header-staff-login"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white transition-colors shadow-sm"
            style={{
              background:
                "linear-gradient(135deg, #299fe0 0%, #cb05a5 130%)",
            }}
          >
            <Lock size={14} />
            Staff Login
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full text-slate-700 hover:bg-slate-100"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.id}`}
                className="px-3 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-50"
              >
                {n.label}
              </a>
            ))}
            <div className="flex gap-2 pt-2">
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-semibold"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
              <Link
                to="/admin/login"
                onClick={() => setOpen(false)}
                className="flex-1 inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-full text-white text-sm font-semibold"
                style={{
                  background:
                    "linear-gradient(135deg, #299fe0 0%, #cb05a5 130%)",
                }}
              >
                <Lock size={14} /> Staff
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
